import { Add, TableRows } from '@mui/icons-material';
import { Box, Button, Divider } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useTranslation } from 'react-i18next';

import { useDataGridAutoHeightToScroll } from '~/components/SolaForceDataGrid/hooks/useDataGridAutoHeightToScroll';
import { useDataGridCRUDActions } from '~/components/SolaForceDataGrid/hooks/useDataGridCRUDActions';
import { SolaForceHeading } from '~/components/SolaForceHeading';
import { useDialogState } from '~/hooks/useDialogState';
import { CompanyFormsDialog } from '~/scenes/PeopleModule/components/Tabs/DocumentsTab/FormsTable/CompanyFormsDialog';
import { EmployeeFormsEntry } from '~/scenes/PeopleModule/components/Tabs/DocumentsTab/FormsTable/data';
import { useFormsTable } from '~/scenes/PeopleModule/components/Tabs/DocumentsTab/FormsTable/useFormsTable';
import { useFormsTableColumns } from '~/scenes/PeopleModule/components/Tabs/DocumentsTab/FormsTable/useFormsTableColumns';
import { LoadingView } from '~/scenes/PortalViews/components/LoadingView';

export const FormsTable = () => {
  const { t } = useTranslation();

  const {
    isLoading,
    downloadDialogConfig,
    columnsConfig,
    crudConfig,
    dataGridProps: dataProps,
  } = useFormsTable();
  const { canDownloadCompanyForms, ...downloadDialogProps } =
    downloadDialogConfig;

  const columnProps = useFormsTableColumns(columnsConfig);

  const {
    dataGridProps,
    toolbarProps: { addNew },
  } = useDataGridCRUDActions<EmployeeFormsEntry, EmployeeFormsEntry['id']>({
    dataGridProps: {
      ...dataProps,
      ...columnProps,
    },
    config: crudConfig,
  });

  const { dataGridProps: autoHeightProps, containerProps } =
    useDataGridAutoHeightToScroll({
      rowsCount: dataGridProps.rows.length,
      settings: {
        scrollRowCount: 10,
        rowHeight: 36,
        headerRowHeight: 39,
      },
    });

  const downloadDialog = useDialogState();

  if (isLoading) {
    return <LoadingView />;
  }

  return (
    <Box>
      <SolaForceHeading heading={t('laForms')}>
        {addNew.available && (
          <Button
            color="primary"
            startIcon={<Add />}
            onClick={() => addNew.onAddNewRow()}
            disabled={addNew.disabled}
          >
            {t('Add')}
          </Button>
        )}
        {addNew.available && canDownloadCompanyForms && (
          <Divider
            orientation={'vertical'}
            variant={'middle'}
            sx={{ height: 'auto', margin: '8px 0' }}
          />
        )}
        {canDownloadCompanyForms && (
          <Button
            color="primary"
            startIcon={<TableRows />}
            onClick={downloadDialog.open}
          >
            {t('laForms')}
          </Button>
        )}
      </SolaForceHeading>
      <Box {...containerProps}>
        <DataGridPro
          {...dataGridProps}
          {...autoHeightProps}
          density={'compact'}
          initialState={{
            pinnedColumns: { right: ['approve', 'actions'] },
            columns: {
              columnVisibilityModel: {
                fileSize: false,
              },
            },
          }}
          hideFooter={true}
        />
      </Box>
      {downloadDialog.isOpen && canDownloadCompanyForms && (
        <CompanyFormsDialog
          {...downloadDialogProps}
          isOpen={downloadDialog.isOpen}
          onClose={downloadDialog.close}
        />
      )}
    </Box>
  );
};
